/* eslint-disable no-undef */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useContext } from 'react';
import { isNil, isEmpty } from 'lodash-es';
import { CommonProps, CommonReturnProps } from 'interfaces/apiFetch';
import { isBusinessActivity } from 'interfaces/onboarding';
import directUpload, { GlobalBlob } from 'utils/directUpload';
import ApplicationContext from 'contexts/ApplicationContext';

interface UploadDocumentsReturn extends CommonReturnProps {
  uploadDocuments: (values: Record<string, any>) => void;
}

type DocumentTypes = 'afs' | 'gis' | 'itr';

interface Document {
  document_type: DocumentTypes;
  file: File;
  signed_id?: string;
}

const useUploadDocuments = (props?: CommonProps): UploadDocumentsReturn => {
  const { setContinueIsLoading } = useContext(ApplicationContext);
  const [isSuccessful, setIsSuccessful] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFailed, setIsFailed] = useState<boolean>(false);
  const [error, setError] = useState<any>(undefined);

  const onSuccessCallback = (params: any): void => {
    if (!isNil(props) && !isNil(props.onSuccessCallback)) {
      props.onSuccessCallback(params);
    }
  };

  const onFailCallback = (): void => {
    if (!isNil(props) && !isNil(props.onFailCallback)) {
      props.onFailCallback();
    }
  };

  const uploadDocuments = async (values: Record<string, any>) => {
    if (!isBusinessActivity(values)) return;
    setContinueIsLoading(true);

    const { afs, gis, itr, ...rest } = values;

    const documents: Document[] = [afs, gis, itr].flatMap((document, index) => {
      const documentType = ['afs', 'gis', 'itr'][index] as DocumentTypes;

      if (isEmpty(document) || isNil(document)) return [];

      return document.map((file: File) => ({
        document_type: documentType,
        file
      }));
    });

    // submit without uploaded documents
    if (isNil(documents) || isEmpty(documents)) {
      onSuccessCallback({ ...rest });
      return;
    }

    try {
      setIsLoading(true);

      const signedDocuments = await Promise.all(
        documents.map(async ({ document_type, file }: Document) => {
          try {
            const signedFileId: GlobalBlob = await directUpload(file);
            return {
              document_type,
              signed_id: signedFileId.signed_id
            };
          } catch (uploadError) {
            setIsFailed(true);
            setError(uploadError);
            throw uploadError;
          }
        })
      );

      const params = {
        ...rest,
        files: signedDocuments
      };

      setIsSuccessful(true);
      setIsLoading(false);
      setIsFailed(false);
      onSuccessCallback({ ...params });
    } catch (catchError) {
      setIsFailed(true);
      setError(catchError);
      setIsLoading(false);
      onFailCallback();
    }
  };

  return {
    isFailed,
    isLoading,
    isSuccessful,
    error,
    uploadDocuments
  };
};

export default useUploadDocuments;
